import { Icon } from '@sapphire-react/core';
import { Wallet } from '@sapphire/icons/react';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';
import type { Control, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSapphireMedia } from '../../hooks';
import { AmountInputField } from '../AmountInputField';

type Props = {
  control: Control<any>;
  fieldFormValue?: string;
  labelText?: string;
};

export function SecondaryApplicantIncome({
  control,
  fieldFormValue,
  labelText,
}: Props) {
  const [secondaryApplicantIncomeNote, setSecondaryApplicantIncomeNote] =
    useState<string>();
  const [isSecondaryApplicantIncomeDirty, setIsSecondaryApplicantIncomeDirty] =
    useState<boolean>();
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const { t } = useTranslation();
  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';

  const secondaryApplicantIncomeRules: Omit<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  > = {
    required: t(
      'shared:personalInformation.inputs.coApplicant.income.requiredError',
    ),
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebouncedSecondaryApplicantIncomeNote = useCallback(
    debounce(
      (value: string) => {
        setSecondaryApplicantIncomeNote(getSecondaryApplicantIncomeNote(value));
        !isSecondaryApplicantIncomeDirty &&
          setIsSecondaryApplicantIncomeDirty(true);
      },
      isSecondaryApplicantIncomeDirty ? 0 : 4000,
    ),
    [isSecondaryApplicantIncomeDirty],
  );

  const getSecondaryApplicantIncomeNote = (income?: string) => {
    if (income && (parseInt(income) > 199999 || parseInt(income) < 8000)) {
      return t('shared:personalInformation.inputs.coApplicant.income.note');
    }

    return undefined;
  };

  return (
    <AmountInputField
      control={control}
      name={'secondaryApplicantIncome'}
      label={
        labelText ??
        t('shared:personalInformation.inputs.coApplicant.income.label')
      }
      rules={secondaryApplicantIncomeRules}
      width={'100%'}
      postfix={'kr'}
      min={0}
      max={100_000_000}
      onBlur={() => setDebouncedSecondaryApplicantIncomeNote('')}
      note={secondaryApplicantIncomeNote}
      prefix={
        <Icon size={inputSize}>
          <Wallet />
        </Icon>
      }
      onFocus={() => {
        setSecondaryApplicantIncomeNote(
          getSecondaryApplicantIncomeNote(fieldFormValue),
        );
      }}
      onChange={(value: string) =>
        setDebouncedSecondaryApplicantIncomeNote(value)
      }
    />
  );
}
